import React from 'react';
import { getBrandFont } from './googleFonts';

/**
 * Shared visual language for the tenant login page.
 *
 * Two surfaces render this design: the real page (`pages/Login.tsx`) and the
 * admin preview (`components/admin/LoginPreview.tsx`). The preview previously
 * hand-copied the palette and layout and drifted a full redesign behind — it was
 * still drawing a centred column with a tree watermark and peer auth tabs long
 * after the page became a two-panel hero + form layout. Everything both surfaces
 * need to agree on lives here so a change lands on both at once.
 *
 * What belongs here: tokens, colour maths, the motif, and the class strings that
 * define the form column's proportions. What does NOT: anything stateful or
 * auth-related — the preview is a static drawing, not a working form.
 */

// --- Colour utilities -------------------------------------------------------
// Tenants pick their own signature colour, so every pairing on this page has to
// be checked at runtime rather than assumed. WCAG relative luminance + contrast.

export type RGB = [number, number, number];

export const parseHex = (hex: string): RGB | null => {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as RGB;
};

export const toHex = (rgb: RGB): string =>
  '#' + rgb.map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('');

export const relLuminance = (rgb: RGB): number => {
  const [R, G, B] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
};

export const contrastRatio = (a: RGB, b: RGB): number => {
  const [hi, lo] = [relLuminance(a), relLuminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

/**
 * Readable foreground (white or ink) for content laid directly on the signature
 * colour. A tenant may pick a light accent (common for dark-theme brands), so the
 * hero text and CTA label can't assume white. Falls back to white on a bad hex.
 */
export function onColour(hex: string): string {
  const rgb = parseHex(hex);
  if (!rgb) return '#FFFFFF';
  return contrastRatio(rgb, [255, 255, 255]) >= contrastRatio(rgb, [10, 10, 10]) ? '#FFFFFF' : '#0A0A0A';
}

/**
 * The signature colour used as TEXT on the page background (overline, links,
 * secondary buttons, focus rings). `onColour` only guards the reverse case —
 * content sitting on the brand — so a light tenant brand would otherwise render
 * near-invisible here. Mixes toward the far end of the scale until it clears
 * `target`, keeping as much of the original hue as possible.
 */
export function readableOn(hex: string, bg: string, target = 4.5, fallback = '#0A0A0A'): string {
  const rgb = parseHex(hex);
  const bgRgb = parseHex(bg);
  if (!bgRgb) return hex;
  if (!rgb) return fallback;
  if (contrastRatio(rgb, bgRgb) >= target) return hex;
  const toward: RGB = relLuminance(bgRgb) > 0.5 ? [0, 0, 0] : [255, 255, 255];
  const mix = (t: number): RGB => rgb.map((c, i) => c + (toward[i] - c) * t) as RGB;
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 12; i++) {
    const mid = (lo + hi) / 2;
    if (contrastRatio(mix(mid), bgRgb) >= target) hi = mid;
    else lo = mid;
  }
  // The search runs in continuous space; rounding to 8-bit hex can drop the
  // result a hundredth below target. Step up until the colour we actually
  // emit clears it.
  let t = hi;
  while (t < 1 && contrastRatio(parseHex(toHex(mix(t)))!, bgRgb) < target) t = Math.min(1, t + 0.01);
  return toHex(mix(t));
}

export const LOGIN_DEFAULT_BRAND = '#1E3A8A';

/**
 * Normalise a tenant branding colour to a canonical `#rrggbb`.
 *
 * On the real page this value reaches the text content of a `<style>` element,
 * which React does NOT escape, so an unvalidated branding string like
 * `red}html{display:none}` would be live CSS — a tenant admin who can only set a
 * colour would gain arbitrary CSS on their own login page. Every derived value
 * (onBrand, accent, the color-mix scrims) inherits the guarantee from here, so
 * this is the single choke point. The preview interpolates the same way and
 * needs the same guard.
 */
export function normaliseBrandColour(raw: string | undefined, fallback = LOGIN_DEFAULT_BRAND): string {
  const parsed = raw ? parseHex(raw) : null;
  return parsed ? toHex(parsed) : fallback;
}

// --- Tokens -----------------------------------------------------------------

export interface LoginTokens {
  bg: string;
  surface: string;
  ink: string;
  ink2: string;
  ink3: string;
  line: string;
}

/**
 * Staff-aligned token palettes (mirror app.css :root / html.dark --staff-* scales).
 * The redesign is light-first; tenants opt into the dark variant via loginTheme.
 * `ink3` is the quietest tier and still carries placeholders, input icons and the
 * "powered by" line, so it is held at >=4.5:1 against both bg and surface — the
 * previous #A3A3A3 measured 2.5:1 and failed AA everywhere it was used.
 */
export const TOKENS: Record<'light' | 'dark', LoginTokens> = {
  light: { bg: '#FAFAF9', surface: '#FFFFFF', ink: '#0A0A0A', ink2: '#525252', ink3: '#737373', line: '#E7E5E4' },
  dark:  { bg: '#0B0B0C', surface: '#141416', ink: '#F4F4F5', ink2: '#A3A3A3', ink3: '#8A8A90', line: '#27272A' },
};

// --- Copy defaults ----------------------------------------------------------
// The placeholders in the admin panel's fields must match these, or an admin who
// leaves a field blank sees one thing in the preview and another in production.

export const LOGIN_DEFAULT_HEADLINE = 'Your future grows here.';
export const LOGIN_DEFAULT_SUBTITLE = 'Access expert-led courses, mentorship, and career pathways.';

/**
 * Default headline face is the staff sans (Rethink Sans); a tenant may override.
 * Note the empty case matters: `getBrandFont(undefined)` resolves to the first
 * entry in BRAND_FONTS (Inter), which is NOT what an unconfigured tenant renders.
 */
export function resolveHeadlineFont(fontId: string | undefined): string {
  return fontId ? getBrandFont(fontId).cssStack : "'Rethink Sans', system-ui, sans-serif";
}

// --- Motif ------------------------------------------------------------------

/**
 * The kula arrow repeated as a "pathways" motif — reversed-out tracks of varying
 * length/opacity, each terminating in a node. Shown on the brand hero when no
 * tenant login image is set.
 */
export const PathwaysMotif: React.FC<{ colour: string }> = ({ colour }) => (
  <svg
    className="absolute right-[-30px] top-0 bottom-0 h-full w-[62%] pointer-events-none"
    viewBox="0 0 300 532"
    preserveAspectRatio="xMaxYMid slice"
    aria-hidden="true"
  >
    <g stroke={colour} fill={colour} strokeWidth="2.5" strokeLinecap="round">
      {([
        [58, 150, 0.14], [112, 86, 0.24], [166, 172, 0.12], [220, 40, 0.30],
        [274, 120, 0.18], [328, 66, 0.26], [382, 182, 0.12], [436, 108, 0.20], [490, 150, 0.14],
      ] as const).map(([y, x1, op]) => (
        <g key={y} opacity={op}>
          <line x1={x1} y1={y} x2={270} y2={y} />
          <circle cx={274} cy={y} r={4.5} stroke="none" />
        </g>
      ))}
    </g>
  </svg>
);

// --- Shared class strings ---------------------------------------------------
// Only the static parts. Anything that needs a token or the accent colour is
// passed as an inline style by the caller, so these stay plain constants.

/** Solid input: white (or dark surface) with a real border + brand focus ring. */
const inputBase =
  'lgn-input w-full rounded-[9px] border pl-11 h-[46px] text-[14px] outline-none transition-all duration-200';

export const loginClasses = {
  /** The form column's width is the page's core proportion — both surfaces share it. */
  formColumn: 'w-full max-w-[332px]',
  overline: 'text-[11px] font-semibold uppercase tracking-[0.14em] mb-3',
  heading: 'font-sans text-[25px] font-semibold tracking-[-0.02em] leading-[1.15] mb-1.5',
  sub: 'text-[13.5px] leading-relaxed',
  label: 'block text-[12px] font-medium mb-1.5',
  input: `${inputBase} pr-4`,
  inputWithAction: `${inputBase} pr-12`,
  inputIcon: 'absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none',
  inputAction:
    'absolute right-1 top-1/2 -translate-y-1/2 h-10 w-10 flex items-center justify-center rounded-md transition-opacity hover:opacity-60',
  primaryButton:
    'w-full h-12 font-semibold tracking-[0.01em] rounded-[9px] flex items-center justify-center gap-2 transition-all duration-200 hover:opacity-95 hover:shadow-lg disabled:opacity-50 disabled:pointer-events-none active:scale-[0.99]',
  /** Quiet, full-width secondary action — never a second brand-filled button. */
  secondaryButton:
    'w-full h-11 rounded-[9px] border text-[13px] font-medium flex items-center justify-center gap-2 transition-colors duration-200 hover:opacity-80',
  /** Inline text button: >=44px tall so it clears the touch-target minimum. */
  inlineLink: 'inline-flex items-center min-h-[44px] text-[13px] font-medium transition-opacity hover:opacity-70',
  footSwitch: 'mt-2 text-[13px]',
  footSwitchLink: 'font-medium underline underline-offset-2 transition-opacity hover:opacity-70 py-2',
  poweredBy: 'mt-6 text-[10px] uppercase tracking-[0.16em] font-medium',
  heroPanel:
    'relative overflow-hidden flex flex-col justify-between px-8 py-9 lg:px-11 lg:py-12 min-h-[210px] lg:min-h-screen lg:flex-[1.02]',
  heroHeadline: 'font-semibold leading-[1.03] tracking-[-0.03em] text-[34px] sm:text-[39px] max-w-[300px]',
  heroFooter: 'mt-4 text-[11px] font-medium uppercase tracking-[0.13em] opacity-70',
  formPanel: 'flex-1 flex flex-col items-center lg:items-start justify-center px-6 py-10 lg:px-[7%]',
} as const;

/**
 * The brand-colour scrim over a tenant login image — heavy at the foot for
 * headline legibility, a thin wash at the top to keep the wordmark readable.
 */
export const heroImageScrim = (brand: string): string =>
  `linear-gradient(to top, color-mix(in srgb, ${brand} 90%, #000) 0%, color-mix(in srgb, ${brand} 35%, transparent) 42%, transparent 68%), ` +
  `linear-gradient(to bottom, color-mix(in srgb, ${brand} 50%, transparent) 0%, transparent 24%)`;

/**
 * Scoped styling for the brand focus ring + solid inputs.
 *
 * `scope` is the selector the rules hang off — `.lgn-scope` on the real page,
 * a generated `#lp-…` id in the preview so several previews can coexist.
 * `brand` and `accent` MUST already be normalised hex (see `normaliseBrandColour`):
 * this string is injected as `<style>` text, which React does not escape.
 */
export function loginScopeCss(scope: string, brand: string, accent: string, T: LoginTokens): string {
  return `
    ${scope} { --login-brand: ${brand}; --login-accent: ${accent}; }
    ${scope} .lgn-input { background: ${T.surface}; border-color: ${T.line}; color: ${T.ink}; }
    ${scope} .lgn-input::placeholder { color: ${T.ink3}; }
    ${scope} .lgn-input:focus {
      border-color: var(--login-accent);
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--login-accent) 32%, transparent);
    }
    /* Every control removed its default ring via outline-none/Tailwind reset;
       put a visible one back for keyboard users. No border-radius here — the
       outline follows the element's own radius. */
    ${scope} button:focus-visible,
    ${scope} a:focus-visible {
      outline: 2px solid var(--login-accent);
      outline-offset: 2px;
    }
    @media (prefers-reduced-motion: reduce) {
      ${scope} *, ${scope} *::before, ${scope} *::after {
        animation-duration: 0.01ms !important;
        transition-duration: 0.01ms !important;
      }
    }
  `;
}
